import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaCalendarAlt } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "../Create.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import {
  CustomButton,
  CustomDatePicker,
  CustomRadioGroup,
  CustomTextInput,
} from "../../../../components";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const ExamConfigurationCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editId = location.state?.ID || null;

  // Get user data from TokenService
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const client_id = clientData?.client_id || "";
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";
  const academic_year_id = clientData?.academic_year_id || "";
  const user_id = user?.id || "";

  const { translations } = useSelector((state) => state.language);

  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    examType: "",
    startDate: null,
    endDate: null,
    isAnnualExam: "0",
    status: "1",
  });
  const [status, setStatus] = useState(0);
  const [errors, setErrors] = useState({});
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });

  // Helper: dates are only mandatory / enabled when status is Active ("1")
  const isInactive = formData.status === "0";

  // If editing, load existing data
  useEffect(() => {
    if (editId) {
      fetchExamConfigDetails();
    }
  }, [editId]);

  // Fetch exam config details for edit mode
  const fetchExamConfigDetails = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.get(
        `/exam/examConfig/view/${editId}`,
      );

      if (response?.data?.status) {
        const data = response.data.data[0] || response.data.data;
        setFormData({
          examType: data.exam_type || "",
          startDate: data.start_date
            ? dayjs(data.start_date).format("YYYY-MM-DD")
            : null,
          endDate: data.end_date
            ? dayjs(data.end_date).format("YYYY-MM-DD")
            : null,
          isAnnualExam: data.is_annual_exam ? "1" : "0",
          status: data.isActive ? "1" : "0",
        });
        setStatus(data.is_status || 0);
      }
    } catch (error) {
      console.error("Error fetching exam config details:", error);
      alert(
        translations.labels?.fetchFailed ||
          "Failed to fetch details. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleDateChange = (name, date) => {
    const formattedDate = date ? dayjs(date).format("YYYY-MM-DD") : null;

    setFormData((prev) => {
      const updated = { ...prev, [name]: formattedDate };

      // If start date changes, clear the end date
      if (name === "startDate") {
        updated.endDate = null;
      }

      return updated;
    });

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleRadioChange = (name, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // If status is set to Inactive, dates become optional:
      // clear both dates so they are not submitted.
      if (name === "status" && value === "0") {
        updated.startDate = null;
        updated.endDate = null;
      }

      return updated;
    });

    // Clear any date validation errors when switching to Inactive
    if (name === "status" && value === "0") {
      setErrors((prev) => ({
        ...prev,
        startDate: "",
        endDate: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.examType || !formData.examType.trim()) {
      newErrors.examType =
        translations.labels?.examTypeRequired || "Exam Type is required";
    }

    // Dates are mandatory ONLY when status is Active.
    // When status is Inactive, dates are optional.
    if (formData.status === "1") {
      if (!formData.startDate) {
        newErrors.startDate =
          translations.labels?.startDateRequired || "Start Date is required";
      }

      if (!formData.endDate) {
        newErrors.endDate =
          translations.labels?.endDateRequired || "End Date is required";
      }
    }

    // The following checks only run if both dates are present
    // (relevant for Active status, or if user partially filled before switching).
    if (
      formData.startDate &&
      formData.endDate &&
      dayjs(formData.endDate).isBefore(dayjs(formData.startDate))
    ) {
      newErrors.endDate =
        translations.labels?.endDateAfterStart ||
        "End Date must be after Start Date";
    }

    // Validate End Date is within 1 year from Start Date
    if (
      formData.startDate &&
      formData.endDate &&
      dayjs(formData.endDate).isAfter(dayjs(formData.startDate).add(1, "year"))
    ) {
      newErrors.endDate =
        translations.labels?.endDateWithinOneYear ||
        "End Date must be within 1 year from Start Date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setSubmitting(true);
    try {
      let response;
      if (editId) {
        // Update payload
        const payload = {
          id: editId,
          client_id: client_id,
          school_id: school_id,
          academic_id: academic_year_id,
          exam_type: formData.examType.trim(),
          is_annual_exam: parseInt(formData.isAnnualExam),
          start_date: formData.startDate,
          end_date: formData.endDate,
          isActive: parseInt(formData.status),
          updated_by: user_id,
        };

        response = await AxiosInstance.put("/exam/examConfig/update", payload);
      } else {
        // Create payload
        const payload = {
          client_id: client_id,
          school_id: school_id,
          academic_id: academic_year_id,
          exam_type: formData.examType.trim(),
          is_annual_exam: parseInt(formData.isAnnualExam),
          start_date: formData.startDate,
          end_date: formData.endDate,
          isActive: parseInt(formData.status),
          created_by: user_id,
        };

        response = await AxiosInstance.post("/exam/examConfig/create", payload);
      }

      if (response?.data?.status || response?.data?.success) {
        setModalState({
          open: true,
          type: "success",
          message: response.data?.message || translations.labels?.updateSuccess,
          redirectPath: "/user/examList",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response.data?.message || translations.labels?.saveFailed,
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editId) return;

    if (
      !window.confirm(
        translations.labels?.confirmDelete ||
          "Are you sure you want to delete this exam configuration?",
      )
    ) {
      return;
    }

    setDeleting(true);

    try {
      const payload = {
        id: editId,
        deleted_by: user_id,
      };

      const response = await AxiosInstance.delete("/exam/examConfig/delete", {
        data: payload,
      });

      if (response?.data?.status || response?.data?.success) {
        setModalState({
          open: true,
          type: "success",
          message:
            translations.labels?.deleteSuccess || "Deleted successfully!",
          redirectPath: "/user/examList",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response?.data?.message || "Failed to delete!",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error deleting:", error);

      setModalState({
        open: true,
        type: "error",
        message:
          translations.labels?.deleteFailed ||
          "Failed to delete. Please try again.",
        redirectPath: "",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    navigate("/user/examList");
  };

  const isAnnualExamOptions = [
    { value: "1", label: translations.labels?.yes || "Yes" },
    { value: "0", label: translations.labels?.no || "No" },
  ];

  const statusOptions = [
    { value: "1", label: translations.labels?.active || "Active" },
    { value: "0", label: translations.labels?.inactive || "Inactive" },
  ];

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.examConfiguration || "Exam Configuration"}
          </h1>
        </div>
        <div className={styles.loadingContainer}>
          <p>{translations.labels?.loading || "Loading..."}</p>
        </div>
      </div>
    );
  }
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };
  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.examConfiguration || "Exam Configuration"}
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* Academic Year (Read-only) */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.academicYear || "Academic Year"}
                name="academicYear"
                type="text"
                value={academic_year}
                disabled
                readOnly
              />
            </div>

            {/* Exam Type/Name */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.examName || "Exam Name"}
                name="examType"
                type="text"
                value={formData.examType}
                onChange={handleInputChange}
                placeholder={
                  translations.labels?.examNamePlaceholder ||
                  "E.g. Half Yearly Exam"
                }
                required
                error={errors.examType}
              />
            </div>

            {/* Start Date */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={translations.labels?.startDate || "Start Date"}
                name="startDate"
                value={formData.startDate ? dayjs(formData.startDate) : null}
                onChange={(date) => handleDateChange("startDate", date)}
                placeholder={
                  translations.labels?.startDatePlaceholder || "Ex: 30/07/2024"
                }
                required={!isInactive}
                disabled={isInactive}
                error={errors.startDate}
              />
            </div>

            {/* End Date */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={translations.labels?.endDate || "End Date"}
                name="endDate"
                value={formData.endDate ? dayjs(formData.endDate) : null}
                onChange={(date) => handleDateChange("endDate", date)}
                placeholder={
                  translations.labels?.endDatePlaceholder || "Ex: 30/08/2024"
                }
                required={!isInactive}
                disabled={isInactive || !formData.startDate}
                error={errors.endDate}
                minDate={formData.startDate ? dayjs(formData.startDate) : null}
                maxDate={
                  formData.startDate
                    ? dayjs(formData.startDate).add(1, "year")
                    : null
                }
              />
            </div>

            {/* Is Annual Exam */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomRadioGroup
                label={
                  translations.labels?.isAnnualExam || "Is This an Annual Exam?"
                }
                name="isAnnualExam"
                value={formData.isAnnualExam}
                onChange={(value) => handleRadioChange("isAnnualExam", value)}
                options={isAnnualExamOptions}
                required
              />
            </div>
          </div>

          {/* Status */}
          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomRadioGroup
                label={translations.labels?.status || "Status"}
                name="status"
                value={formData.status}
                onChange={(value) => handleRadioChange("status", value)}
                options={statusOptions}
                required
              />
            </div>
          </div>

          {/* Note - Show only in edit mode */}
          {/* {editId && (
            <div className={styles.noteContainer}>
              <p className={styles.noteText}>
                <span className={styles.noteLabel}>
                  {translations.labels?.note || "Note"}:
                </span>{" "}
                {translations.labels?.examConfigEditNote ||
                  "The exam schedule has already been created. Editing the Status is not allowed."}
              </p>
            </div>
          )} */}

          {/* Buttons */}
          <div className={styles.buttonContainer}>
            <CustomButton
              type="submit"
              variant="contained"
              className={styles.submitButton}
              fullWidth={false}
              loading={submitting}
              loadingText={
                editId
                  ? translations.labels?.updating || "Updating..."
                  : translations.labels?.submitting || "Submitting..."
              }
              disabled={status !== 0}
            >
              {editId
                ? translations.labels?.update || "UPDATE"
                : translations.labels?.submit || "SUBMIT"}
            </CustomButton>

            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {translations.labels?.cancel || "CANCEL"}
            </CustomButton>

            {/* Delete Button - Only show in edit mode */}
            {editId && (
              <CustomButton
                type="button"
                variant="contained"
                className={styles.deleteButton}
                fullWidth={false}
                onClick={handleDelete}
                loading={deleting}
                loadingText={translations.labels?.deleting || "Deleting..."}
                disabled={status !== 0}
              >
                {translations.labels?.delete || "DELETE"}
              </CustomButton>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default ExamConfigurationCreate;
